import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FacilityFormType, GridConnectionApplicationType, InstallationFlowType, TableConfig, UserRoles } from '../constants/app.constants';
import { CommonService } from '../services/common.service';
import { CommunicationService } from '../services/communication.service';
import { LocalStorageService } from '../services/local-storage.service';
import { AllDropdownKeys } from '../jurisdiction/jurisdiction.constants';
import {
  EmployeeListResponse,
  OrderProcessAppExportField,
  OrderProcessApplication,
  OrderProcessApplicationsResponse,
  OrderProcessExportCompareType,
  OrderProcessExportFilter,
  UpdateLockStatusResponse,
} from './jurisdiction-overview.domain';
import { ConfirmationService, SelectItem, Table } from 'primeng';
import { ErrorService } from '../services/error/error.service';
import * as _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { TableConfiguration } from '../domains/common.domain';
import { NetzTranslateService } from '../services/netz-translate.service';
import { forkJoin, Observable } from 'rxjs';
import { EnvironmentService } from '../services/environment.service';
import { ChimneySweeperFilters } from '../interface/config.interface';
import { publishReplay, refCount, map } from 'rxjs/operators';
import { TableFilterType } from '../+shared/enums/table-filter-type.enum';
import { FileService } from '../services/file.service';
import { HttpResponse } from '@angular/common/http';

@Component({
  providers: [ConfirmationService],
  selector: 'app-jurisdiction-overview',
  templateUrl: './jurisdiction-overview.component.html',
  styleUrls: ['./jurisdiction-overview.component.scss'],
})
export class JurisdictionOverviewComponent implements OnInit, AfterViewInit {
  @ViewChild('jurisdictionOverview') private jurisdictionTable: Table;

  allApplications: OrderProcessApplication[];
  applicationTypeDetailsKey: string;
  executorActionsKey: string;
  filterFields: string[];
  branchFilter: string[];
  branchFilterOptions: SelectItem[];
  branchFilterOptions$: Observable<SelectItem[]> = new Observable<SelectItem[]>();
  branchFilterText: string;
  employeesFilterOptions: SelectItem[];
  employeesFilterOptions$: Observable<SelectItem[]> = new Observable<SelectItem[]>();
  employeesListData: SelectItem[];
  processFilter: string[];
  processFilterOptions: SelectItem[];
  processFilterOptions$: Observable<SelectItem[]> = new Observable<SelectItem[]>();
  processFilterText: string;
  typeFilter: string[];
  typeFilterOptions: SelectItem[];
  typeFilterOptions$: Observable<SelectItem[]> = new Observable<SelectItem[]>();
  typeFilterText: string;
  globalFilterValue: string;
  highlightedRowIndex: number;
  isArchiveView: boolean;
  isLoading: boolean;
  isReadOnly: boolean;
  isZeroApplication: boolean;
  orderProcessesKey: string;
  role: string;
  selectedEmployeeFilter: SelectItem[];
  showFilterByLocationIcon: boolean;
  showFilters: boolean;
  technicianId: string;
  tableConfig: TableConfiguration;
  mainActiveFilter: string;
  hideTechnicianColumn: boolean = this.environmentService.hideTechnicianColumn;
  showCompanyNameColumn: boolean = this.environmentService.showCompanyNameColumn;
  showTechnicianColumn: boolean = this.environmentService.showTechnicianColumn;

  gridCompStatusKey: string = 'grid_comp_status';
  regepStatusKey: string = 'registration_of_energy_producer_status';
  appColumns: any[] = [
    { field: OrderProcessAppExportField.application_id_text },
    { field: OrderProcessAppExportField.location_text },
    { field: OrderProcessAppExportField.applicant_text },
    { field: OrderProcessAppExportField.technician_text },
    { field: OrderProcessAppExportField.company_name_text },
    { field: OrderProcessAppExportField.type_text },
    { field: OrderProcessAppExportField.branch_text },
    { field: OrderProcessAppExportField.process_text },
    { field: OrderProcessAppExportField.last_changed_text },
    { field: OrderProcessAppExportField.desired_date_text },
  ];
  selectedLastChangedDate: string;
  lastChangedDateFilterOptions: any[];
  selectedLastChangedFilterType: string;
  selectedDesiredDate: string;
  desiredDateFilterOptions: any[];
  selectedDesiredDateFilterType: string;

  private branchNoOfFiltersSelected: number;
  private originalApplicationsList: OrderProcessApplication[];
  private processNoOfFiltersSelected: number;
  private typeNoOfFiltersSelected: number;
  private viewApplicationDetails: boolean;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router,
    private cookieService: CookieService,
    private netzTranslateService: NetzTranslateService,
    private environmentService: EnvironmentService,
    private fileService: FileService
  ) {
    this.localStorageService.removeItem('application_id');
    this.localStorageService.removeItem('application_type');
    this.localStorageService.removeItem('selected_technician_id');
  }

  onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  ngAfterViewInit(): void {
    const data$ = this.jurisdictionTable.onFilter.pipe(
      map(() => {
        if (!this.mainActiveFilter) {
          return this.allApplications;
        }

        if (this.mainActiveFilter === 'sk_application_type_detail' && this.typeFilter?.length) {
          return this.allApplications.filter(a => this.typeFilter.indexOf(a.sk_application_type_detail) > -1);
        }

        if (this.mainActiveFilter === 'branch' && this.branchFilter?.length) {
          return this.allApplications.filter(a => a.branch.some(b => this.branchFilter.indexOf(b) > -1));
        }

        if (this.mainActiveFilter === 'sk_orderprocess_status' && this.processFilter?.length) {
          return this.allApplications.filter(a => this.processFilter.indexOf(a.sk_orderprocess_status) > -1);
        }

        if (this.mainActiveFilter === 'employee' && this.selectedEmployeeFilter?.length) {
          return this.allApplications.filter(a => a.employee && this.selectedEmployeeFilter.indexOf(a.employee.value) > -1);
        }

        if (this.mainActiveFilter === 'last_changed' && this.selectedLastChangedDate?.length) {
          if (this.selectedLastChangedFilterType) {
            const date = new Date(this.selectedLastChangedDate);
            if (this.selectedLastChangedFilterType === TableFilterType.LessThan) {
              return this.allApplications.filter(a => new Date(a.last_changed).getTime() < date.getTime());
            }
            if (this.selectedLastChangedFilterType === TableFilterType.GreaterThan) {
              return this.allApplications.filter(a => new Date(a.last_changed).getTime() > date.getTime());
            }
          }
          return this.allApplications.filter(a => a.last_changed.includes(this.selectedLastChangedDate));
        }

        if (this.mainActiveFilter === 'desired_date' && this.selectedDesiredDate?.length) {
          if (this.selectedDesiredDateFilterType) {
            const date = new Date(this.selectedDesiredDate);
            if (this.selectedDesiredDateFilterType === TableFilterType.LessThan) {
              return this.allApplications.filter(a => new Date(a.desired_date).getTime() < date.getTime());
            }
            if (this.selectedDesiredDateFilterType === TableFilterType.GreaterThan) {
              return this.allApplications.filter(a => new Date(a.desired_date).getTime() > date.getTime());
            }
          }
          return this.allApplications.filter(a => a.desired_date === this.selectedDesiredDate);
        }
        return this.allApplications;
      }),
      publishReplay(1),
      refCount()
    );

    this.typeFilterOptions$ = data$.pipe(
      map(value => {
        if (!this.mainActiveFilter || this.mainActiveFilter === 'sk_application_type_detail') {
          return this.typeFilterOptions;
        }

        const applicableValues = value.map(value => value.sk_application_type_detail).filter(this.onlyUnique);

        return this.typeFilterOptions.filter(f => applicableValues.indexOf(f.value) > -1);
      })
    );

    this.branchFilterOptions$ = data$.pipe(
      map(value => {
        if (!this.mainActiveFilter || this.mainActiveFilter === 'branch') {
          return this.branchFilterOptions;
        }

        const applicableValues = value
          .map(value => value.branch)
          .reduce((accumulator, current) => [...accumulator, ...current], [])
          .filter(this.onlyUnique);

        return this.branchFilterOptions.filter(f => applicableValues.indexOf(f.value) > -1);
      })
    );

    this.processFilterOptions$ = data$.pipe(
      map(value => {
        if (!this.mainActiveFilter || this.mainActiveFilter === 'sk_orderprocess_status') {
          return this.processFilterOptions;
        }

        const applicableValues = value.map(value => value.sk_orderprocess_status).filter(this.onlyUnique);

        return this.processFilterOptions.filter(f => applicableValues.indexOf(f.value) > -1);
      })
    );

    this.employeesFilterOptions$ = data$.pipe(
      map(value => {
        if (!this.mainActiveFilter || this.mainActiveFilter === 'employee') {
          return this.employeesFilterOptions;
        }

        const applicableValues = value.map(value => value.employee.value).filter(this.onlyUnique);
        return this.employeesFilterOptions.filter(f => f.value === '' || applicableValues.indexOf(f.value) > -1);
      })
    );

    this.prepareFilters();
  }

  checkAndViewApplication() {
    const applicationId = this.route.snapshot?.queryParams?.application_id;
    if (!applicationId) return;

    const selectedApplication = this.allApplications?.find(x => x.application_id === Number(applicationId));
    if (!selectedApplication) return;

    return this.viewApplication(selectedApplication);
  }

  get showEmployeeColumn(): boolean {
    return ['administrator', 'employee'].indexOf(this.role.toLowerCase()) > -1;
  }

  ngOnInit(): void {
    this.role = this.localStorageService.getItem('role');
    this.showFilters =
      this.role.toLowerCase() !== 'customer' || (this.role.toLowerCase() === 'customer' && this.environmentService.showFiltersForCustomer);
    this.technicianId = this.localStorageService.getItem('technician_id');
    this.filterFields = [
      'application_id',
      'application_ext_id',
      'company_name',
      'applicant',
      'technician',
      'connection_location',
      'employee',
      'sk_application_type_detail',
      'applicant',
      'status',
      'branch',
      'sk_orderprocess_status',
      'last_changed',
      'grid_comp_area',
    ];
    this.isReadOnly = false;
    this.isZeroApplication = true;
    this.viewApplicationDetails = false;
    this.applicationTypeDetailsKey = AllDropdownKeys.ApplicationTypeDetail;
    this.executorActionsKey = AllDropdownKeys.ExecutorType;
    this.orderProcessesKey = AllDropdownKeys.OrderProcess;
    this.highlightedRowIndex = -1;
    this.showFilterByLocationIcon = true;
    this.employeesListData = [
      {
        label: 'Select',
        value: '',
      },
    ];
    this.tableConfig = TableConfig;
    this.typeFilterOptions = [];
    this.branchFilterOptions = [];
    this.processFilterOptions = [];
    this.employeesFilterOptions = [];
    this.selectedEmployeeFilter = null;

    this.branchFilter = [];
    this.processFilter = [];
    this.typeFilter = [];
    this.lastChangedDateFilterOptions = this.getDateFilterTypes('last_changed');
    this.selectedLastChangedFilterType = this.lastChangedDateFilterOptions[0].value;
    this.desiredDateFilterOptions = this.getDateFilterTypes('desired_date');
    this.selectedDesiredDateFilterType = this.desiredDateFilterOptions[0].value;

    this.registerForRoute();
    this.initializeCustomFilter();
    this.registerForLanguageChange();
  }

  evaluateActionButtonState(selectedApplication: OrderProcessApplication): boolean {
    const allExecutors = selectedApplication.executor.split(',');
    return (
      (selectedApplication.locked === '0' || this.technicianId === selectedApplication.lockedby) &&
      allExecutors.indexOf('system') === -1 &&
      allExecutors.indexOf(this.role) > -1
    );
  }

  viewApplication(selectedApplication: OrderProcessApplication): void {
    this.isReadOnly = !this.evaluateActionButtonState(selectedApplication);

    if (selectedApplication.application_sub_type === 'gridcomp') {
      this.router.navigate([`/gridcomp/technical-data/${selectedApplication.application_id}`]);
    } else if (selectedApplication.application_sub_type === 'reg_energy_producer') {
      this.router.navigate([`/regep/technical-data/${selectedApplication.application_id}`]);
    } else if (selectedApplication.application_sub_type === 'drr_ccd_device') {
      this.localStorageService.setItem('installation_flow', 'consumptionDevices');
      this.localStorageService.setItem('application_id', selectedApplication.application_id?.toString());
      this.router.navigate([`/drr-ccd-devices/details/${selectedApplication.application_id}`]);
    } else if (this.isReadOnly || (!this.isReadOnly && this.technicianId === selectedApplication.lockedby)) {
      this.viewApplicationDetails = true;
      this.handleSelectedApplication(selectedApplication);
    } else if (selectedApplication.locked === '0') {
      this.viewApplicationDetails = true;
      this.updateProcessingStatus(selectedApplication);
    }
  }

  confirmApplicationArchive(applicationId: string): void {
    this.confirmationService.confirm({
      accept: () => {
        this.updateArchiveState(applicationId);
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  updateArchiveState(applicationId: string): void {
    const requestUrl = this.isArchiveView
      ? `application/unarchive?application_id=${applicationId}`
      : `application/archive?application_id=${applicationId}`;
    this.isLoading = true;

    this.communicationService.updateData(requestUrl, null).subscribe(
      response => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.getData();
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  updateProcessingStatus(selectedApplication: OrderProcessApplication): void {
    if (this.role === 'administrator' || selectedApplication.locked === '0' || this.technicianId === selectedApplication.lockedby) {
      const requestUrl =
        selectedApplication.locked === '0'
          ? `application/lock?application_id=${selectedApplication.application_id}`
          : `application/unlock?application_id=${selectedApplication.application_id}`;
      this.isLoading = true;

      this.communicationService.updateData(requestUrl, null).subscribe(
        (response: UpdateLockStatusResponse) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);

          if (response && response.application && response.application.length) {
            selectedApplication.locked = response.application[0].locked;
            selectedApplication.lockedby = response.application[0].lockedby;
          }
          this.handleSelectedApplication(selectedApplication);
        },
        error => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    }
  }

  onSelectEmployee(employeeData: SelectItem, applicationId: number) {
    this.isLoading = true;
    const employeeId = employeeData.value;
    const requestData = {
      assigned_employee_id: employeeId,
    };

    this.communicationService.updateData(`application/assign?application_id=${applicationId}`, requestData).subscribe(
      (response: OrderProcessApplicationsResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.allApplications.forEach(item => {
          if (item.application_id === applicationId) {
            item.assigned_employee_id = employeeId;
          }
        });
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  filterList(location: string): void {
    this.allApplications = _.cloneDeep(
      this.originalApplicationsList.filter(
        application => _.isEqual(location, application.connection_location) || _.isEqual(location, application.grid_comp_area)
      )
    );
    this.showFilterByLocationIcon = false;
  }

  resetList(): void {
    this.localStorageService.removeItem('employee-filter');
    if (this.showEmployeeColumn) {
      this.selectedEmployeeFilter = [];
    }
    this.resetColumnFilters();
    this.allApplications = [...this.originalApplicationsList];
    this.showFilterByLocationIcon = true;
    this.mainActiveFilter = null;
  }

  onFilterChange(event, column: string, resetCookie: boolean = true) {
    if (!this.mainActiveFilter) {
      this.mainActiveFilter = column;
      this.setCookie('jurisdiction-overview.mainActiveFilter', this.mainActiveFilter);
    }

    let selectedFilterValues, filterCheck;
    if (column === 'last_changed' || column === 'desired_date') {
      selectedFilterValues = event ? this.commonService.parseDateForSaving(new Date(event)) : '';
      if (column === 'last_changed') {
        filterCheck = this.selectedLastChangedFilterType;
        this.lastChangedDateFilterOptions.filter(f => f.value !== filterCheck).map(f => (f.icon = ''));
      } else {
        filterCheck = this.selectedDesiredDateFilterType;
        this.desiredDateFilterOptions.filter(f => f.value !== filterCheck).map(f => (f.icon = ''));
      }
    } else {
      selectedFilterValues = [...event.value];
      if (column === 'branch') {
        selectedFilterValues.sort((a, b) => {
          const value1 = FacilityFormType.filter(item => item.key === a)[0];
          const value2 = FacilityFormType.filter(item => item.key === b)[0];
          return value1.index - value2.index;
        });
      }
      filterCheck = column === 'branch' ? TableFilterType.Contains : column === 'employee' ? TableFilterType.Custom : TableFilterType.In;
    }

    this.jurisdictionTable.filter(selectedFilterValues, column, filterCheck);
    this.setFilterText(column);

    if (resetCookie) {
      const jurisdictionFilter = JSON.stringify({ value: selectedFilterValues, filter: filterCheck });
      this.deleteCookie(column);
      this.setCookie(column, jurisdictionFilter, 6050);
    }
  }

  public exportToCSV(): void {
    this.isLoading = true;
    const filter = this.mapJurisdictionFilters();
    this.fileService.getOrderProcessFile(filter).subscribe(
      (response: HttpResponse<Blob>) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, false);
        const fileName = this.fileService.getFileNameFromHeader(response.headers);
        this.fileService.downloadDocument(response.body, fileName, true);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private mapJurisdictionFilters(): OrderProcessExportFilter {
    const typeFilter = this.jurisdictionTable.filters['sk_application_type_detail'];
    const branchFilter = this.jurisdictionTable.filters['branch'];
    const processStatusFilter = this.jurisdictionTable.filters['sk_orderprocess_status'];
    const lastChangedFilter = this.jurisdictionTable.filters['last_changed'];
    const desiredFilter = this.jurisdictionTable.filters['desired_date'];

    let exportFilter = new OrderProcessExportFilter();
    exportFilter.language = this.netzTranslateService.getCurrentLang();
    if (this.jurisdictionTable.sortField) {
      exportFilter.order_bys.push({
        prop_name: this.jurisdictionTable.sortField,
        is_ascending: this.jurisdictionTable.sortOrder > 0,
      });
    }
    if (typeFilter) {
      exportFilter.filter_bys.push({
        prop_name: 'sk_application_type_detail',
        compare_type: OrderProcessExportCompareType.in,
        value: typeFilter.value.join(','),
      });
    }
    if (processStatusFilter) {
      exportFilter.filter_bys.push({
        prop_name: 'sk_orderprocess_status',
        compare_type: OrderProcessExportCompareType.in,
        value: processStatusFilter.value.join(','),
      });
    }
    if (lastChangedFilter) {
      exportFilter.filter_bys.push({
        prop_name: 'last_changed',
        compare_type: OrderProcessExportCompareType[lastChangedFilter.matchMode],
        value: lastChangedFilter.value,
      });
    }
    if (desiredFilter) {
      exportFilter.filter_bys.push({
        prop_name: 'desired_date',
        compare_type: OrderProcessExportCompareType[desiredFilter.matchMode],
        value: desiredFilter.value,
      });
    }
    if (branchFilter) {
      branchFilter.value.map(value => {
        if (value === 'heating') {
          exportFilter.filter_bys.push({
            prop_name: 'districtheat',
            compare_type: OrderProcessExportCompareType.contains,
            value: '1',
          });
        } else {
          exportFilter.filter_bys.push({
            prop_name: value,
            compare_type: OrderProcessExportCompareType.contains,
            value: '1',
          });
        }
      });
    }

    return exportFilter;
  }

  private onDateFilterTypeChange(item: any, column: string): void {
    item.icon = 'pi pi-check';
    if (column === 'last_changed') {
      this.selectedLastChangedFilterType = item.value;
      this.onFilterChange(this.selectedLastChangedDate, 'last_changed');
    } else {
      this.selectedDesiredDateFilterType = item.value;
      this.onFilterChange(this.selectedDesiredDate, 'desired_date');
    }
  }

  private getDateFilterTypes(column: string): any[] {
    return [
      {
        label: this.netzTranslateService.translateString('overview.jurisdiction-overview.equals'),
        value: TableFilterType.Contains,
        icon: 'pi pi-check',
        command: event => {
          this.onDateFilterTypeChange(event.item, column);
        },
      },
      {
        label: this.netzTranslateService.translateString('overview.jurisdiction-overview.less-than'),
        value: TableFilterType.LessThan,
        command: event => {
          this.onDateFilterTypeChange(event.item, column);
        },
      },
      {
        label: this.netzTranslateService.translateString('overview.jurisdiction-overview.greater-than'),
        value: TableFilterType.GreaterThan,
        command: event => {
          this.onDateFilterTypeChange(event.item, column);
        },
      },
    ];
  }

  private setCookie(key: string, value: any, expires?: number | Date) {
    const pageSpecificKey = this.getPageSpecificCookieKey(key);
    this.cookieService.set(pageSpecificKey, value, expires);
  }

  private getCookie(key: string): string {
    const pageSpecificKey = this.getPageSpecificCookieKey(key);
    return this.cookieService.get(pageSpecificKey);
  }

  private deleteCookie(key: string): void {
    const pageSpecificKey = this.getPageSpecificCookieKey(key);
    this.cookieService.delete(pageSpecificKey);
  }

  private getPageSpecificCookieKey(key: string) {
    return this.isArchiveView ? 'archived-' + key : 'all-' + key;
  }

  private getInstallationFlowType(application: OrderProcessApplication): string {
    const [{ value: installationApplicationType }, { value: connectionApplicationType }] = this.commonService.getDropdownData(
      AllDropdownKeys.ApplicationType
    );

    if (application.sk_application_type === installationApplicationType) {
      const applicationSubTypeInstallationFlowMap = {
        [GridConnectionApplicationType.New]: InstallationFlowType.NewInstallation,
        [GridConnectionApplicationType.Change]: InstallationFlowType.UpdateInstallation,
        [GridConnectionApplicationType.FeedInSystem]: InstallationFlowType.FeedInSystem,
        [GridConnectionApplicationType.SiteElectricityMeterdata]: InstallationFlowType.SiteElectricityMeterdata,
      };

      return applicationSubTypeInstallationFlowMap[application.application_sub_type];
    }

    if (application.sk_application_type === connectionApplicationType) {
      if (application.application_sub_type == GridConnectionApplicationType.Change) {
        const applicationTypesDetail = {
          reallocation: 'connection_change_reallocation',
          reinforce: 'connection_change_reinforce',
          shutdown: 'connection_change_shutdown',
          conversionToGas: 'connection_change_conversion_gas',
          conversionToHeat: 'connection_change_conversion_heat',
        };

        if (
          application.sk_application_type_detail == applicationTypesDetail.reallocation ||
          application.sk_application_type_detail == applicationTypesDetail.reinforce ||
          application.sk_application_type_detail == applicationTypesDetail.shutdown
        ) {
          return InstallationFlowType.ChangeHouseConnection;
        }

        if (
          application.sk_application_type_detail == applicationTypesDetail.conversionToGas ||
          application.sk_application_type_detail == applicationTypesDetail.conversionToHeat
        ) {
          return InstallationFlowType.ConversionToGas;
        }
      }

      const applicationSubTypeInstallationFlowMap = {
        [GridConnectionApplicationType.New]: InstallationFlowType.MainsConnection,
        [GridConnectionApplicationType.Temporary]: InstallationFlowType.TemporaryConnection,
      };
      return applicationSubTypeInstallationFlowMap[application.application_sub_type];
    }
  }

  private getData(): void {
    const requestUrl = this.isArchiveView
      ? 'application?archived=1'
      : ['customer', 'standard_technician', 'guest_technician'].indexOf(this.role) > -1
      ? 'application'
      : 'application?archived=0';

    this.isLoading = true;

    if (this.jurisdictionTable) {
      this.resetColumnFilters(false);
    }

    const allSubscriptions: Observable<any>[] = [];
    const applicationsListSubscription = this.communicationService.getData(requestUrl);
    allSubscriptions.push(applicationsListSubscription);

    if (this.showEmployeeColumn) {
      const employeesListSubscription = this.communicationService.getData('employeeuser');
      allSubscriptions.push(employeesListSubscription);
    }

    forkJoin(allSubscriptions).subscribe(
      (response: [OrderProcessApplicationsResponse, EmployeeListResponse]) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response[0], true);
        this.originalApplicationsList = response[0].application || [];

        this.highlightedRowIndex = this.commonService.showHighlightedRow;
        this.commonService.showHighlightedRow = -1;

        if (response[1] && response[1].employeeuser && response[1].employeeuser.length) {
          response[1].employeeuser.forEach(item => {
            this.employeesListData.push({
              label: item.first_name || item.last_name ? `${item.first_name} ${item.last_name}` : '-',
              value: item.technician_id.toString(),
            });
          });
          this.setEmployeeFilter();
        }
        this.parseData();
        this.checkAndViewApplication();
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  private parseData(): void {
    if (this.originalApplicationsList.length) {
      const facilities = FacilityFormType;
      this.isZeroApplication = false;

      this.originalApplicationsList.forEach(item => {
        item.employee = this.employeesListData.find(element => element.value === item.assigned_employee_id);
        item.branch = [];
        item.actions = '';
        item.application_id = Number(item.application_id);
        item.grid_comp_area = item.grid_comp_area && item.grid_comp_area != '' ? item.grid_comp_area : null;
        if (item.sk_application_type === 'gridcomp_feedin') {
          item.sk_orderprocess_status = item[this.gridCompStatusKey];
        } else if (item.sk_application_type === 'registration_of_energy_producer') {
          item.sk_orderprocess_status = item[this.regepStatusKey];
        }

        facilities.forEach(facility => {
          const key = facility.key !== 'heating' ? facility.key : 'districtheat';
          if (item[key] === '1') {
            item.branch.push(facility.key);
          }
        });
      });
    }

    this.allApplications = [...this.originalApplicationsList];

    if (this.showFilters) {
      this.setFilter();
    }
  }

  private handleSelectedApplication(selectedApplication: OrderProcessApplication): void {
    if (this.viewApplicationDetails) {
      this.commonService.orderProcessData = selectedApplication;
      this.localStorageService.setItem('application_id', selectedApplication.application_id.toString());
      this.localStorageService.setItem('application_type', selectedApplication.sk_application_type);
      this.localStorageService.setItem(
        'selected_technician_id',
        selectedApplication.technician_id || selectedApplication.resource_owner_user_id
      );
      this.localStorageService.setItem('application_type_detail', selectedApplication.sk_application_type_detail);

      const installationFlow = this.getInstallationFlowType(selectedApplication);
      this.localStorageService.setItem('installation_flow', installationFlow);

      const selectedFacilities = FacilityFormType.filter(item => {
        const key = item.key !== 'heating' ? item.key : 'districtheat';
        if (selectedApplication[key] === '1') {
          return item;
        }
      });
      if (selectedFacilities.length) {
        this.localStorageService.setItem('selected_facilities', selectedFacilities);
      }

      this.viewApplicationDetails = false;
      this.router.navigate(['/process-details'], {
        queryParams: {
          isReadOnly: this.isReadOnly,
          orderProcessId: selectedApplication.orderprocess_id,
        },
      });
    } else {
      this.getData();
    }
  }

  private registerForRoute(): void {
    this.route.url.subscribe(() => {
      if (this.route.snapshot.firstChild) {
        this.isArchiveView =
          this.route.snapshot.firstChild.url &&
          this.route.snapshot.firstChild.url.length &&
          this.route.snapshot.firstChild.url[0].path === 'archive';
      } else {
        this.isArchiveView = false;
      }

      this.showFilterByLocationIcon = true;
      this.getData();
    });
  }

  private prepareFilters() {
    const applicationTypesDetail = this.commonService.getDropdownData(this.applicationTypeDetailsKey);
    const orderProcesses = this.commonService.getDropdownData(this.orderProcessesKey);
    const chimneySweeperFilters: ChimneySweeperFilters = this.environmentService.chimneySweeperFilters;
    this.typeFilterOptions = [];
    this.branchFilterOptions = [];
    this.processFilterOptions = [];

    FacilityFormType.forEach(item => {
      if (this.role === UserRoles.chimneySweeper) {
        if (chimneySweeperFilters.branchTypeFilters.indexOf(item.key) > -1) {
          this.branchFilterOptions.push({
            label: this.netzTranslateService.translateString(`overview.details-data.data-rows.${item.key}`),
            value: item.key,
          });
        }
      } else {
        this.branchFilterOptions.push({
          label: this.netzTranslateService.translateString(`overview.details-data.data-rows.${item.key}`),
          value: item.key,
        });
      }
    });
    orderProcesses.forEach(item => {
      if (this.role === UserRoles.chimneySweeper) {
        if (chimneySweeperFilters.orderProcessTypeFilters.indexOf(item.value) > -1) {
          this.processFilterOptions.push({
            label: this.netzTranslateService.translateString(`lookup.sk_orderprocess_status.options.${item.value}.name`),
            value: item.value,
          });
        }
      } else {
        this.processFilterOptions.push({
          label: this.netzTranslateService.translateString(`lookup.sk_orderprocess_status.options.${item.value}.name`), // item.label,
          value: item.value,
        });
      }
    });
    applicationTypesDetail.forEach(item => {
      const displayApplicationType = this.environmentService.applicationTypeFilters.find(elem => elem.key === item.value && elem.display);
      if (displayApplicationType?.display) {
        if (this.role === UserRoles.chimneySweeper) {
          if (chimneySweeperFilters.applicationTypeFilters.indexOf(item.value) > -1) {
            this.typeFilterOptions.push({
              label: this.netzTranslateService.translateString(`lookup.sk_application_type_detail.options.${item.value}.name`),
              value: item.value,
            });
          }
        } else {
          this.typeFilterOptions.push({
            label: this.netzTranslateService.translateString(`lookup.sk_application_type_detail.options.${item.value}.name`),
            value: item.value,
          });
        }
      }
    });
  }

  private setFilter() {
    const branchColumnFilter = this.getCookie('branch');
    const processColumnFilter = this.getCookie('sk_orderprocess_status');
    const typeColumnFilter = this.getCookie('sk_application_type_detail');
    const employeeFilter = this.getCookie('employee');
    const lastChangedFilter = this.getCookie('last_changed');
    const desiredDateFilter = this.getCookie('desired_date');
    const branchFilter = branchColumnFilter.length ? JSON.parse(branchColumnFilter) : null;
    const processFilter = processColumnFilter.length ? JSON.parse(processColumnFilter) : null;
    const typeFilter = typeColumnFilter.length ? JSON.parse(typeColumnFilter) : null;
    const selectedEmployeeFilter = employeeFilter?.length ? JSON.parse(employeeFilter) : null;
    const selectedLastChangedDate = lastChangedFilter?.length ? JSON.parse(lastChangedFilter) : null;
    const selectedDesiredDate = desiredDateFilter?.length ? JSON.parse(desiredDateFilter) : null;

    this.mainActiveFilter = this.getCookie('jurisdiction-overview.mainActiveFilter');

    this.branchFilter = branchFilter?.value ?? [];
    this.processFilter = processFilter?.value ?? [];
    this.typeFilter = typeFilter?.value ?? [];
    this.selectedEmployeeFilter = selectedEmployeeFilter?.value ?? [];
    this.selectedLastChangedDate = selectedLastChangedDate?.value;
    this.selectedLastChangedFilterType = selectedLastChangedDate?.filter;
    this.lastChangedDateFilterOptions[0].icon = '';
    this.lastChangedDateFilterOptions.filter(f => f.value === this.selectedLastChangedFilterType).map(f => (f.icon = 'pi pi-check'));
    this.selectedDesiredDate = selectedDesiredDate?.value;
    this.selectedDesiredDateFilterType = selectedDesiredDate?.filter;
    this.desiredDateFilterOptions[0].icon = '';
    this.desiredDateFilterOptions.filter(f => f.value === this.selectedDesiredDateFilterType).map(f => (f.icon = 'pi pi-check'));

    this.jurisdictionTable.filter(this.branchFilter, 'branch', branchFilter?.filter);
    this.setFilterText('branch');
    this.jurisdictionTable.filter(this.processFilter, 'sk_orderprocess_status', processFilter?.filter);
    this.setFilterText('sk_orderprocess_status');
    this.jurisdictionTable.filter(this.typeFilter, 'sk_application_type_detail', typeFilter?.filter);
    this.setFilterText('sk_application_type_detail');
    this.jurisdictionTable.filter(this.selectedEmployeeFilter, 'employee', selectedEmployeeFilter?.filter);
    this.setFilterText('employee');
    this.jurisdictionTable.filter(this.selectedLastChangedDate, 'last_changed', this.selectedLastChangedFilterType);
    this.jurisdictionTable.filter(this.selectedDesiredDate, 'desired_date', this.selectedDesiredDateFilterType);
  }

  private initializeCustomFilter(): void {
    if (this.showEmployeeColumn) {
      this.commonService.initializeCustomFilter(this.filterFunction);
    }
  }

  private filterFunction = (value: SelectItem, filter: SelectItem[]): boolean => {
    if (!filter?.length) {
      return true;
    }
    return filter.indexOf(value.value) > -1;
  };

  private registerForLanguageChange(): void {
    this.netzTranslateService.registerForLanguageChange().subscribe(() => {
      this.prepareFilters();
      this.setFilterText('branch');
      this.setFilterText('sk_orderprocess_status');
      this.setFilterText('sk_application_type_detail');
      if (this.showEmployeeColumn) {
        this.setEmployeeFilter();
      }
    });
  }

  private resetColumnFilters(resetCookie: boolean = true) {
    const filterValue = {
      value: [],
    };
    this.branchFilter.length = 0;
    this.processFilter.length = 0;
    this.typeFilter.length = 0;
    this.selectedLastChangedDate = null;
    this.lastChangedDateFilterOptions[0].icon = 'pi pi-check';
    this.lastChangedDateFilterOptions.slice(1).map(f => (f.icon = ''));
    this.selectedLastChangedFilterType = this.lastChangedDateFilterOptions[0].value;
    this.selectedDesiredDate = null;
    this.desiredDateFilterOptions[0].icon = 'pi pi-check';
    this.desiredDateFilterOptions.slice(1).map(f => (f.icon = ''));
    this.selectedDesiredDateFilterType = this.desiredDateFilterOptions[0].value;
    this.onFilterChange(filterValue, 'branch', resetCookie);
    this.onFilterChange(filterValue, 'sk_orderprocess_status', resetCookie);
    this.onFilterChange(filterValue, 'sk_application_type_detail', resetCookie);
    this.onFilterChange(filterValue, 'employee', resetCookie);
    this.onFilterChange(this.selectedLastChangedDate, 'last_changed', resetCookie);
    this.onFilterChange(this.selectedDesiredDate, 'desired_date', resetCookie);
    this.jurisdictionTable.reset();
    this.jurisdictionTable.filter([], 'employee', TableFilterType.Custom);
    this.globalFilterValue = '';
  }

  private setFilterText(column: string) {
    switch (column) {
      case 'branch':
        if (this.branchFilter.length) {
          this.branchNoOfFiltersSelected = this.branchFilter.length;
          this.branchFilterText =
            this.branchNoOfFiltersSelected +
            ' ' +
            (this.branchNoOfFiltersSelected === 1
              ? this.netzTranslateService.translateString('overview.filter-text.single')
              : this.netzTranslateService.translateString('overview.filter-text.multiple'));
        } else {
          this.branchFilterText = 'Select';
          this.branchNoOfFiltersSelected = 0;
        }

        const branchMultiSelectItem = document.getElementById('branchMultiSelectFilter');
        const branchSelectLabelElement =
          (branchMultiSelectItem && branchMultiSelectItem.getElementsByClassName('ui-multiselect-label')) || [];

        if (branchSelectLabelElement.length) {
          branchSelectLabelElement[0].innerHTML = this.branchFilterText;
        }
        break;
      case 'sk_orderprocess_status':
        if (this.processFilter.length) {
          this.processNoOfFiltersSelected = this.processFilter.length;
          this.processFilterText =
            this.processNoOfFiltersSelected +
            ' ' +
            (this.processNoOfFiltersSelected === 1
              ? this.netzTranslateService.translateString('overview.filter-text.single')
              : this.netzTranslateService.translateString('overview.filter-text.multiple'));
        } else {
          this.processNoOfFiltersSelected = 0;
          this.processFilterText = 'Select';
        }

        const processMultiSelectItem = document.getElementById('processMultiSelectFilter');
        const processSelectLabelElement =
          (processMultiSelectItem && processMultiSelectItem.getElementsByClassName('ui-multiselect-label')) || [];

        if (processSelectLabelElement.length) {
          processSelectLabelElement[0].innerHTML = this.processFilterText;
        }
        break;
      case 'sk_application_type_detail':
        if (this.typeFilter.length) {
          this.typeNoOfFiltersSelected = this.typeFilter.length;
          this.typeFilterText =
            this.typeNoOfFiltersSelected +
            ' ' +
            (this.typeNoOfFiltersSelected === 1
              ? this.netzTranslateService.translateString('overview.filter-text.single')
              : this.netzTranslateService.translateString('overview.filter-text.multiple'));
        } else {
          this.typeNoOfFiltersSelected = 0;
          this.typeFilterText = 'Select';
        }

        const typeMultiSelectItem = document.getElementById('typeMultiSelectFilter');
        const typeSelectLabelElement = (typeMultiSelectItem && typeMultiSelectItem.getElementsByClassName('ui-multiselect-label')) || [];

        if (typeSelectLabelElement.length) {
          typeSelectLabelElement[0].innerHTML = this.typeFilterText;
        }
        break;
    }
  }

  private setEmployeeFilter(): void {
    this.employeesFilterOptions = [
      {
        label: this.netzTranslateService.translateString('overview.jurisdiction-overview.my_orders'),
        value: this.technicianId,
      },
      {
        label: this.netzTranslateService.translateString('overview.jurisdiction-overview.unassigned-orders'),
        value: '',
      },
      ...this.employeesListData.slice(1),
    ];
  }
}
